import { adminApi } from './api';
import type {
  Referral,
  ReferralDetail,
  ReferralStats,
  ReferralUser,
  ReferralUserDetail,
  ReferralListResponse,
  ReferralUserListResponse,
  ReferralFilters,
  ReferralUserFilters,
  ReferralReward,
  AdjustCreditsInput,
  ChangeTierInput,
  RejectReferralInput,
} from '../types/referral.types';

export const referralService = {
  // Stats
  async getStats(): Promise<ReferralStats> {
    const { data } = await adminApi.get<ReferralStats>('/admin/referrals/stats');
    return data;
  },

  // Referrals
  async getReferrals(filters?: ReferralFilters): Promise<ReferralListResponse> {
    const params = new URLSearchParams();
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.referrerId) params.append('referrerId', filters.referrerId);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const { data } = await adminApi.get<ReferralListResponse>(
      `/admin/referrals${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  },

  async getReferral(id: string): Promise<ReferralDetail> {
    const { data } = await adminApi.get<ReferralDetail>(`/admin/referrals/${id}`);
    return data;
  },

  async validateReferral(id: string): Promise<Referral> {
    const { data } = await adminApi.post<Referral>(`/admin/referrals/${id}/validate`);
    return data;
  },

  async rejectReferral(id: string, input: RejectReferralInput): Promise<Referral> {
    const { data } = await adminApi.post<Referral>(`/admin/referrals/${id}/reject`, input);
    return data;
  },

  async validateAllPending(): Promise<{ validated: number }> {
    const { data } = await adminApi.post<{ validated: number }>('/admin/referrals/validate-all');
    return data;
  },

  // Users
  async getUsers(filters?: ReferralUserFilters): Promise<ReferralUserListResponse> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.tier && filters.tier !== 'all') params.append('tier', filters.tier);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const { data } = await adminApi.get<ReferralUserListResponse>(
      `/admin/referrals/users${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  },

  async getUser(userId: string): Promise<ReferralUserDetail> {
    const { data } = await adminApi.get<ReferralUserDetail>(`/admin/referrals/users/${userId}`);
    return data;
  },

  async adjustCredits(userId: string, input: AdjustCreditsInput): Promise<ReferralUser> {
    const { data } = await adminApi.post<ReferralUser>(
      `/admin/referrals/users/${userId}/adjust-credits`,
      input
    );
    return data;
  },

  async changeTier(userId: string, input: ChangeTierInput): Promise<ReferralUser> {
    const { data } = await adminApi.post<ReferralUser>(
      `/admin/referrals/users/${userId}/change-tier`,
      input
    );
    return data;
  },

  // Rewards
  async getRewards(): Promise<ReferralReward[]> {
    const { data } = await adminApi.get<{ rewards: ReferralReward[] }>('/admin/referrals/rewards');
    return data.rewards;
  },
};

export default referralService;
